import moment from 'moment';

/**
 * The single boundary between the coupon API's shapes and this page's.
 *
 * The API stores numeric enums, epoch milliseconds and 0/1 flags; the components below are ported
 * from a prototype that used strings and booleans throughout. Rather than teach every component
 * both dialects, everything crossing in or out of `services/otherService` passes through here.
 *
 * Nothing else in this folder should read `item.type`, `item.status`, `item.isPrivate` or a raw
 * date off an API response.
 */

/* ---------- type ---------- */

// couponModel.type: 1 is amount, 0 is percentage. Matches the existing table's `item.type ? ...`.
export const TYPE_AMOUNT = 'Amount';
export const TYPE_PERCENTAGE = 'Percentage';

export const COUPON_TYPES = [TYPE_PERCENTAGE, TYPE_AMOUNT];

const typeToLabel = value => (Number(value) === 1 ? TYPE_AMOUNT : TYPE_PERCENTAGE);
const labelToType = label => (label === TYPE_AMOUNT ? 1 : 0);

/* ---------- dates ---------- */

/**
 * Dates arrive as epoch milliseconds and go back out as `MM-DD-YYYY`, which is what
 * `coupon/create` expects — the server re-parses that string through `appUtils.getTimeStampStart`
 * and `getTimeStampEnd` before saving. Native `<input type="date">` speaks a third dialect,
 * `YYYY-MM-DD`, so all three conversions live here.
 *
 * Everything is read and written in local time on purpose: `getTimeStampStart` builds its
 * timestamp with `new Date(dateString)` on the server, so a director picking "June 1" means
 * midnight where the server is, and shifting to UTC here would land the wrong calendar day either
 * side of the boundary.
 */
const toMoment = value => {
    if (value === null || value === undefined || value === '') return null;
    // Epoch ms from the API is an exact instant — always read back in UTC, the same reference
    // frame the server pins it in (`getTimeStampEnd`/`getTimeStampStart`), so the calendar day
    // shown doesn't depend on the viewer's own timezone. A YYYY-MM-DD string from the picker has
    // no time component to begin with; parsing it against that explicit format — never the
    // ambiguous `new Date(dateString)`, which silently reinterprets it in local time — reads it
    // as exactly the calendar day it says.
    const parsed = typeof value === 'number' ? moment.utc(value) : moment.utc(value, 'YYYY-MM-DD');
    return parsed.isValid() ? parsed : null;
};

/** Epoch ms (or anything date-like) to the `YYYY-MM-DD` a native date input reads and writes. */
export const toDateInputValue = value => {
    const parsed = toMoment(value);
    return parsed ? parsed.format('YYYY-MM-DD') : '';
};

/** A `YYYY-MM-DD` form value to the `MM-DD-YYYY` the create/edit endpoints expect. */
export const toApiDate = value => {
    const parsed = toMoment(value);
    return parsed ? parsed.format('MM-DD-YYYY') : '';
};

/** Epoch ms to `Jun 1, 2026` for display. Returns null so callers can choose their own fallback. */
export const formatDate = value => {
    const parsed = toMoment(value);
    return parsed ? parsed.format('MMM D, YYYY') : null;
};

export const formatDateRange = (start, end) => {
    const from = formatDate(start);
    const to = formatDate(end);
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return 'No dates set';
};

/* ---------- usage limits ---------- */

/**
 * `noOfTimesUsed` is the *limit*, not the count — `couponUsedByUser` is the count. Legacy rows
 * predate the field being required, so it arrives as null, undefined or 0.
 *
 * null/undefined means "no limit was ever set" and reads as unlimited. A stored 0 is ambiguous —
 * literally "zero redemptions allowed", which no director sets deliberately — and dividing by it
 * would blow up the usage meter, so it is treated as unlimited too.
 */
const toLimit = value => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

/* ---------- API -> UI ---------- */

/**
 * One row from `coupon/getCouponsList`. The projection there returns the fields used below and
 * nothing more, so this is also the list of what the page can render without a per-row detail call.
 */
export const fromApi = item => ({
    id: item._id,
    name: item.couponName || '',
    code: item.couponCode || '',
    type: typeToLabel(item.type),
    value: Number(item.value) || 0,
    redeemed: Number(item.couponUsedByUser) || 0,
    possibleUsage: toLimit(item.noOfTimesUsed),
    perUserUsage: toLimit(item.maxUseageByUser),
    startDate: item.startDate ?? null,
    endDate: item.endDate ?? null,
    active: Number(item.status) === 1,
    isPrivate: Number(item.isPrivate) === 1,
    refundable: Number(item.refund) === 1,
    created: item.created ?? null,
});

/* ---------- UI -> API ---------- */

/**
 * A validated draft to the body `coupon/create` expects.
 *
 * `campId` and `programId` are JSON-stringified arrays because `couponValidator.validateCreate`
 * calls `JSON.parse` on them before the facade sees them — the same contract the previous form
 * used. `additionalExp` is only sent when non-empty, and `isAdditionalExp` gates it server-side.
 */
export const toCreateParams = (draft, { campCode, campId }) => {
    // "All programs" means exactly that, whatever is still sitting in the targeting fields. They
    // stay populated in the draft so that toggling the radio back and forth does not lose the
    // selection, which makes clearing them the submit step's job rather than the radio's.
    const targeted = draft.scope === 'specific';

    const params = {
        campCode,
        campId: JSON.stringify([campId]),
        couponName: draft.name.trim(),
        couponCode: draft.code.trim(),
        // category/subCategory come from react-select (ObjectId options), programType from a native
        // select holding the numeric code straight out of `helper.programTypes`.
        //
        // `null`, NOT `''`, when unset. These are ObjectId paths on couponModel, and mongoose 6
        // throws a CastError on an empty string there — so sending '' made `coupon/create` fail
        // outright for every coupon without a category, which is most of them. (The old form got
        // away with `categoryId.value` on a string state, which is `undefined` and therefore
        // dropped by JSON.stringify. Same effect, by accident.) `programType` and `userCode` below
        // are String paths, where '' is a legitimate empty value.
        categoryId: targeted && draft.category ? draft.category.value : null,
        subcategoryId: targeted && draft.subCategory ? draft.subCategory.value : null,
        userCode: draft.userCode.trim(),
        programType: targeted ? draft.programType : '',
        type: labelToType(draft.couponType),
        value: Number(draft.value),
        startDate: toApiDate(draft.startDate),
        endDate: toApiDate(draft.endDate),
        noOfTimesUsed: Number(draft.possibleUsage),
        // Left blank rather than 0 when unset: the server treats a missing maxUseageByUser as
        // "no per-user cap" (`appUtils.exists` guard in couponFacade), and a 0 would read as a cap.
        maxUseageByUser: draft.perUserUsage === '' ? '' : Number(draft.perUserUsage),
        refund: draft.refundable ? 1 : 0,
        isPrivate: draft.isPrivate ? 1 : 0,
        programId: JSON.stringify(targeted ? draft.programs.map(i => i.value) : []),
    };

    if (targeted && draft.experiences.length) {
        params.additionalExp = JSON.stringify(draft.experiences.map(i => i.value));
        params.isAdditionalExp = true;
    }

    return params;
};

/**
 * A validated draft to the body `coupon/edit` expects — and *only* that.
 *
 * Six fields, deliberately. Everything else a coupon has can change what an open cart is priced
 * at: `cartFacade` re-reads the live coupon document on every cart render, so moving `value`,
 * `type`, `couponCode`, `refund` or the targeting would move the total under a family already in
 * checkout. Those changes go through clone instead.
 *
 * The server enforces the same list (`couponDao.EDITABLE_FIELDS`) and its route destructures only
 * these, so this is the third of three gates rather than the only one. It is still spelled out
 * here rather than spreading the draft, because a spread is how the other two stop being enough.
 *
 * `maxUseageByUser` goes as '' rather than 0 when blank: mongoose casts '' to null on a Number
 * path, which is what "no per-user cap" means, whereas 0 would read as a cap of zero.
 */
export const toEditParams = (draft, coupon) => ({
    couponId: coupon.id,
    couponName: draft.name.trim(),
    startDate: toApiDate(draft.startDate),
    endDate: toApiDate(draft.endDate),
    noOfTimesUsed: Number(draft.possibleUsage),
    maxUseageByUser: draft.perUserUsage === '' ? '' : Number(draft.perUserUsage),
    isPrivate: draft.isPrivate ? 1 : 0,
});

/* ---------- detail -> draft ---------- */

/**
 * One `coupon/getCouponData` record to the draft the form edits.
 *
 * Used by edit, and by clone, which is why it rebuilds the react-select option shapes rather than
 * settling for the display names the endpoint also returns. Names cannot round-trip: they are
 * ambiguous across camps, and the pipeline's `$lookup`s drop anything that no longer resolves, so
 * a draft rebuilt from names would quietly widen a three-program coupon to every program. The
 * ObjectIds this reads (`programIds`, `campIds`, `categoryId`, `subcategoryId`, `additionalExp`)
 * were added to the projection for exactly this.
 *
 * `programList` and `categories` are the same sources the create form's pickers use, so an option
 * built here is the identical object the picker would have produced.
 */
export const fromDetail = (detail, { programList = [], categories = [] } = {}) => {
    // $addToSet over an $unwind that preserves empties can put a null in the array.
    const programIds = (detail.programIds || []).filter(Boolean).map(String);
    const experienceIds = (detail.additionalExp || []).filter(Boolean).map(String);

    const programs = programList
        .filter(item => programIds.includes(String(item._id)))
        .map(item => ({ ...item, value: item._id, label: item.programName }));

    // Experiences hang off programs, so they are resolved from the programs just matched — the
    // same rule the form's own picker follows.
    const experiences = programs
        .flatMap(program => program.additionalExp || [])
        .filter(item => experienceIds.includes(String(item._id)))
        .map(item => ({ value: item._id, label: item.title }));

    const categoryRecord = (categories || []).find(
        item => String(item._id) === String(detail.categoryId)
    );
    const subCategoryRecord = (categoryRecord?.subCategory || []).find(
        item => String(item._id) === String(detail.subcategoryId)
    );

    const category = categoryRecord
        ? { value: categoryRecord._id, label: categoryRecord.name }
        : null;
    const subCategory = subCategoryRecord
        ? { value: subCategoryRecord._id, label: subCategoryRecord.name }
        : null;

    const programType = detail.programType ? String(detail.programType) : '';

    return {
        name: detail.couponName || '',
        code: detail.couponCode || '',
        // The code is never regenerated from the name here. In edit mode it is locked; in clone
        // mode it has already been made unique and must not be overwritten by the next keystroke.
        codeEdited: true,
        couponType: typeToLabel(detail.type),
        value: detail.value === null || detail.value === undefined ? '' : String(detail.value),
        startDate: toDateInputValue(detail.startDate),
        endDate: toDateInputValue(detail.endDate),
        scope:
            programs.length || experiences.length || category || subCategory || programType
                ? 'specific'
                : 'all',
        programs,
        experiences,
        category,
        subCategory,
        programType,
        userCode: detail.userCode || '',
        possibleUsage: toDraftNumber(detail.noOfTimesUsed),
        perUserUsage: toDraftNumber(detail.maxUseageByUser),
        isPrivate: Number(detail.isPrivate) === 1,
        refundable: Number(detail.refund) === 1,
    };
};

/**
 * A stored numeric limit to the string a text input holds.
 *
 * null/undefined mean "never set" and belong in an empty box. A stored 0 is the ambiguous case —
 * `toLimit` above already reads it as unlimited for display, so it is shown blank here too rather
 * than as a literal 0 the director never typed.
 */
function toDraftNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? String(numeric) : '';
}
